import { useEffect, useState } from "react";
import { useUserData } from "../auth";
import { supabase } from "../supabase";
import {
  SOCIAL_FIELDS,
  SOCIAL_LABELS,
  type SocialField,
} from "../utils/clientSocial";
export type SocialConfig = {
  field_key: SocialField;
  field_label: string;
  is_visible: boolean;
  is_required: boolean;
  sort_order: number;
};
export const defaultSocialFields = (): SocialConfig[] =>
  SOCIAL_FIELDS.map((key, i) => ({
    field_key: key,
    field_label: SOCIAL_LABELS[key],
    is_visible: true,
    is_required: false,
    sort_order: i,
  }));
const EMPTY_FIELDS: SocialConfig[] = [];
export function useSocialFields() {
  const { organizationId } = useUserData();
  const [result, setResult] = useState<{
    org: string | null;
    fields: SocialConfig[];
    error: string | null;
  }>({ org: null, fields: [], error: null });
  useEffect(() => {
    let active = true;
    const load = async () => {
      if (!organizationId) return;
      const { data, error } = await supabase
        .from("client_form_field_configurations")
        .select("field_key,field_label,is_visible,is_required,sort_order")
        .eq("organization_id", organizationId)
        .eq("field_section", "social");
      if (active)
        setResult({
          org: organizationId,
          error: error?.message || null,
          fields: defaultSocialFields()
            .map((f) => ({
              ...f,
              ...data?.find((x) => x.field_key === f.field_key),
            }))
            .sort((a, b) => a.sort_order - b.sort_order),
        });
    };
    void load();
    window.addEventListener("client-social-config-changed", load);
    return () => {
      active = false;
      window.removeEventListener("client-social-config-changed", load);
    };
  }, [organizationId]);
  return {
    organizationId,
    fields: result.org === organizationId ? result.fields : EMPTY_FIELDS,
    error: result.org === organizationId ? result.error : null,
    loaded: !!organizationId && result.org === organizationId,
  };
}
